import { memo, useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { useCommonLogin } from 'hooks/UseCommonLogin';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/LoginValidator';
import { useReceiveAuthorizeRequest } from 'hooks/UseReceiveAuthorizeRequest';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import { callCheckWhiteListApi } from 'requests/RequestWhiteList';
import { HttpStatus } from 'consts/HttpStatus';
import { convertObjectToGetParameterWithEncode } from 'common/ObjectToGetParameterConverter';
import { unescapeHtml } from 'common/HtmlEscaper';
import { GetLoginDisplayItemResData, callGetLoginDisplayItemApi } from 'requests/RequestLogin';
import { ReactError } from 'consts/ReactError';
import { saveGenerator as loginDisplayItemSaveGenerator } from 'store/connect/LoginDisplayItemData';
import classes from './override.module.scss';

type Props = {};

export type LoginParameters = {
	gakkenId: string;
	password: string;
};

export const CommonLoginTemplate = memo((_props: Props) => {
	const [store, dispatch] = useStore(); // eslint-disable-line
	const history = useHistory();
	const { isV1ParamsOk, isV2ParamsOk, burl, bsn, scd, redirect_uri, client_id } = useReceiveAuthorizeRequest(dispatch);

	const [displayItem, setDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	const [displayItemLoading, setDisplayItemLoading] = useState(false);
	useEffect(() => {
		if (store.authorizeV2Data.client_id) {
			const execute = async () => {
				setDisplayItemLoading(true);
				const result = await callGetLoginDisplayItemApi({
					gid_dp: store.authorizeV2Data.gid_dp,
					site_cd: store.authorizeV2Data.client_id,
				});
				if (result.status !== HttpStatus.OK || !result.data) {
					setActionError(ReactError.E0008);
					history.push(RoutePath.ERROR);
					setDisplayItemLoading(false);
					return;
				}
				setDisplayItem(result.data);
				loginDisplayItemSaveGenerator(dispatch, result.data);
				setDisplayItemLoading(false);
			};
			execute();
		}
	}, [store.authorizeV2Data.client_id]);

	const [formData, setFormData] = useState<LoginParameters>({ gakkenId: '', password: '' });
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [processLoading, setProcessLoading] = useState(false);
	const { login } = useCommonLogin(
		formErrors,
		setFormErrors,
		getErrorMessage,
		formData,
		store,
		actionError,
		setActionError,
		isV1ParamsOk,
		setProcessLoading,
		displayItem,
	);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({ ...formData, [event.target.name]: event.target.value });
	};

	useEffect(() => {
		const execute = async () => {
			setProcessLoading(true);
			// パラメータが飛んでこなかった場合はエラー
			if (!isV1ParamsOk && !isV2ParamsOk) {
				history.push(RoutePath.ERROR);
				setProcessLoading(false);
				return;
			}
			if (isV1ParamsOk) {
				const result = await callCheckWhiteListApi({
					burl,
					bsn,
					scd,
				});
				if (result.status === HttpStatus.REDIRECT) {
					history.push(RoutePath.ERROR);
					return;
				}
				setProcessLoading(false);
				return;
			}
			if (isV2ParamsOk) {
				const result = await callCheckWhiteListApi({
					burl: redirect_uri,
					scd: client_id,
				});
				if (result.status === HttpStatus.REDIRECT) {
					history.push(RoutePath.ERROR);
					return;
				}
				setProcessLoading(false);
				return;
			}
		};
		execute();
	}, [isV1ParamsOk, isV2ParamsOk]);

	const [isPassword, setIsPassword] = useState<boolean>(true);
	const togglePassword = () => {
		setIsPassword((prev) => !prev);
	};

	const toRegister = () => {
		// 新規登録のリンクがあればそちらに遷移する
		if (displayItem?.initial_registration_url) {
			window.location.href = displayItem.initial_registration_url;
			return;
		}

		if (isV1ParamsOk) {
			const params = convertObjectToGetParameterWithEncode(store.authorizeV1Data, 'burl');
			history.push(`${RoutePath.USER_REGISTER_1}?${params}`);
			return;
		}
		if (isV2ParamsOk) {
			const authorizeV2Data = { ...store.authorizeV2Data };
			authorizeV2Data.gid_act = 'register';
			const params = convertObjectToGetParameterWithEncode(authorizeV2Data, 'code_challenge', 'redirect_uri', 'nonce');
			history.push(`${RoutePath.USER_REGISTER_1}?${params}`);
			return;
		}
		history.push(RoutePath.USER_REGISTER_1);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = `GakkenIDログイン`;
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					{/* bsnにHTMLタグ情報も含まれている可能性があるためdangerouslySetを使用 */}
					{isV1ParamsOk ? (
						<div className="section-head" dangerouslySetInnerHTML={{ __html: unescapeHtml(bsn) }} />
					) : null}
					{isV2ParamsOk ? (
						<>
							<div className="section-head">
								{displayItem?.logo ? <img src={displayItem?.logo} alt={displayItem?.title} /> : null}
								{displayItem?.title ? (
									<p style={{ fontSize: displayItem?.title_size, fontWeight: displayItem?.title_weight }}>
										{displayItem?.title}
									</p>
								) : null}
							</div>
							<div className="free-box">
								<div
									className="txt"
									style={{
										fontSize: displayItem?.description_size,
										fontWeight: displayItem?.description_weight,
										whiteSpace: 'pre-line',
									}}
								>
									{displayItem?.description}
								</div>
							</div>
						</>
					) : null}

					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.gakkenId !== '' ? <p>【GakkenID】{formErrors.gakkenId}</p> : null}
										{formErrors.password !== '' ? <p>【パスワード】{formErrors.password}</p> : null}
									</div>
								</>
							) : null}
						</div>
					) : null}

					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">GakkenID</label>
						</div>
						<div className="form-input">
							<input type="text" name="gakkenId" onChange={handleChangeValue} value={formData.gakkenId} />
						</div>
						<p className="error">{formErrors.gakkenId}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">パスワード</label>
						</div>
						<div className="form-input">
							<input
								type={isPassword ? 'password' : 'text'}
								name="password"
								onChange={handleChangeValue}
								value={formData.password}
							/>
							<span className={`icon-password ${isPassword ? '' : 'is-active'}`} onClick={togglePassword}></span>
						</div>
						<p className="error">{formErrors.password}</p>
					</div>
					<div className="btn-box">
						<div className="btn gr">
							{processLoading || displayItemLoading ? <CircularProgress /> : <button onClick={login}>ログイン</button>}
						</div>
					</div>

					{isV1ParamsOk && (
						<div className="new-member-txt">
							<a onClick={toRegister}>新規会員登録はこちら</a>
						</div>
					)}
					{isV2ParamsOk && displayItem?.initial_registration_display_flg === 1 && (
						<div className="new-member-txt">
							<a onClick={toRegister}>
								{displayItem.initial_registration_link_display
									? displayItem.initial_registration_link_display
									: '新規会員登録はこちら'}
							</a>
						</div>
					)}
					{(isV1ParamsOk || (isV2ParamsOk && displayItem?.forget_password_display_flg === 1)) && (
						<>
							<div className="forgot-txt">
								<Link to={RoutePath.FORGOT_ID}>GakkenIDをお忘れの方はこちら</Link>
							</div>
							<div className={classes.passwordForgotTxt}>
								<Link to={RoutePath.FORGOT_PASSWORD}>パスワードをお忘れの方はこちら</Link>
							</div>
						</>
					)}
				</div>
			</section>
		</>
	);
});
